import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { Button } from "components/DesignSystem/Button/Button";
import Card from "components/DesignSystem/Card/Card";
import Modal from "components/DesignSystem/Modal/Modal";
import { useToast } from "hooks/useToast";
import { DashboardLayout } from "components/DashboardLayout";
import { Header } from "components/DesignSystem/Header/Header";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import {
  useGetBookkeepingLogsQuery,
  useGetBookkeepingSettingsQuery,
  useUpdateBookkeepingSettingsMutation,
} from "store/apis/booksLockDate";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { BackendError } from "types/utils/error";
import { Field, Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useModal } from "hooks/useModal";
import { formatDate } from "utils/formatDate";
import { EntitySelector } from "components/EntitySelector/EntitySelector";
import { Trash } from "components/icons/Trash";
import { DeleteIcon } from "components/icons/delete";
import { useAuth } from "hooks/useAuth";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import Condition from "yup/lib/Condition";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { ConditionalLink } from "components/conditionalLink";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import classNames from "classnames";
import { Pencil20 } from "components/icons/Pencil20";
import { Trash20 } from "components/icons/Trash20";

// Form validation schema
const LockDateSchema = Yup.object().shape({
  accounting_lock_date: Yup.string().required("Lock date is required"),
});

// Form values interface
type LockDateFormValues = {
  accounting_lock_date: string;
};

export const BooksLockDate = ({
  showEntitySelector = true,
}: {
  showEntitySelector?: boolean;
}) => {
  const editModal = useModal();
  const confirmModal = useModal();
  const removeModal = useModal();

  const { successToast, alertToast } = useToast();
  const { uuid } = useAuth();
  const { isAdmin } = useRoleBasedView();

  const entityId = useCurrentEntityId();
  const group = useCurrentGroupContext();

  const {
    data: lockDateData,
    isLoading,
    isSuccess,
    refetch,
  } = useGetBookkeepingSettingsQuery({ entityId }, { skip: !entityId });

  const [updateLockDate, { isLoading: isUpdating }] =
    useUpdateBookkeepingSettingsMutation();

  const { data: logsData } = useGetBookkeepingLogsQuery(
    { entityId },
    { skip: !entityId }
  );

  const handleEditClick = () => {
    editModal.open();
  };

  const handleRemoveLock = () => {
    removeModal.open();
  };

  const handleAddLockDateClick = () => {
    editModal.open();
  };

  const formValues = {
    accounting_lock_date: lockDateData?.accounting_lock_date || "",
  };

  const confirmDeleteLock = async () => {
    if (!entityId) return;
    try {
      await updateLockDate({
        entityId,
        accounting_lock_date: null,
      }).unwrap();

      removeModal.close();
      successToast({
        title: "Lock date removed",
        message: "Your financials are now unlocked.",
      });
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ||
            "Failed to remove lock date",
        },
        error as Error
      );
    }
  };

  const handleConfirmSubmit = async (
    values: LockDateFormValues,
    { setSubmitting }: FormikHelpers<LockDateFormValues>
  ) => {
    if (!entityId) {
      setSubmitting(false);
      return;
    }

    try {
      await updateLockDate({
        entityId,
        accounting_lock_date: values.accounting_lock_date,
      }).unwrap();

      editModal.close();
      confirmModal.close();
      successToast({
        title: "Lock date updated",
        message: `Your financials are now locked until ${dayjs(
          values.accounting_lock_date
        ).format("d MMM, YYYY")}`,
      });

      refetch();
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ||
            "Failed to update lock date",
        },
        error as Error
      );
    } finally {
      setSubmitting(false);
    }
  };

  const canUpdate = isAdmin || logsData?.logs?.[0]?.changed_by.uuid === uuid;

  return (
    <DashboardLayout
      header={
        <Header
          v2
          title="Books Settings"
          right={showEntitySelector ? <EntitySelector /> : null}
        />
      }
    >
      <div className="t-text-subtitle">Lock Financials</div>

      <p className="t-text-body-sm t-text-text-30 t-mb-4">
        We'll lock everything up to (and including) that day for everyone to
        keep your financials clean and final.
      </p>

      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isEmpty={!lockDateData?.accounting_lock_date}
      >
        <Async.Empty>
          <div className="t-mt-6">
            <Button customType="primary" onClick={handleAddLockDateClick}>
              Add lock date
            </Button>
          </div>
        </Async.Empty>
        <Async.Success>
          {lockDateData?.accounting_lock_date ? (
            <div className="t-flex t-items-center t-gap-2">
              <div className="t-flex t-flex-col t-gap-1">
                <Formik
                  onSubmit={() => {}}
                  initialValues={formValues || {}}
                  enableReinitialize
                >
                  <Form>
                    <Field name="accounting_lock_date">
                      {({ field }: any) => (
                        <DateInput
                          {...field}
                          portalId={field.name}
                          placeholder="DD-MMM-YYYY"
                          // dateFormat={DD_MMM_YYYY}
                          disabled
                          customSize="small"
                        />
                      )}
                    </Field>
                  </Form>
                </Formik>
              </div>

              <ConditionalToolTip
                propClass="!t-text-left"
                condition={
                  !canUpdate && (
                    <div className="t-flex t-gap-2 t-flex-col">
                      <p className="t-m-0 t-text-subtitle">Access Restricted</p>
                      <p className="t-m-0 t-text-body-sm t-w-64">
                        You don't have access to edit the lock date. Please
                        reach out to us via{" "}
                        <ConditionalLink
                          className="t-text-white !t-underline"
                          to={`/chat/${group.secondary_channel_url}`}
                        >
                          chat
                        </ConditionalLink>{" "}
                        for assistance.
                      </p>
                    </div>
                  )
                }
              >
                <span>
                  <Button
                    size="extra-small"
                    onClick={handleEditClick}
                    customType="ghost_icon"
                    disabled={!canUpdate}
                  >
                    <span
                      className={classNames({
                        "t-text-text-30": canUpdate,
                        "t-text-neutral-30": !canUpdate,
                      })}
                    >
                      <Pencil20 strokeWidth="1.2" color="currentColor" />
                    </span>
                  </Button>
                </span>
              </ConditionalToolTip>

              <ConditionalToolTip
                propClass="!t-text-left"
                condition={
                  !canUpdate && (
                    <div className="t-flex t-gap-2 t-flex-col">
                      <p className="t-m-0 t-text-subtitle">Access Restricted</p>
                      <p className="t-m-0 t-text-body-sm t-w-64">
                        You don't have access to edit the lock date. Please
                        reach out to us via{" "}
                        <ConditionalLink
                          className="t-text-white !t-underline"
                          to={`/chat/${group.secondary_channel_url}`}
                        >
                          chat
                        </ConditionalLink>{" "}
                        for assistance.
                      </p>
                    </div>
                  )
                }
              >
                <span>
                  <Button
                    size="extra-small"
                    onClick={handleRemoveLock}
                    customType="ghost_icon"
                    disabled={!canUpdate}
                  >
                    <span
                      className={classNames({
                        "t-text-text-30": canUpdate,
                        "t-text-neutral-30": !canUpdate,
                      })}
                    >
                      <Trash20 />
                    </span>
                  </Button>
                </span>
              </ConditionalToolTip>
            </div>
          ) : (
            <div className="t-mt-6">
              <Button customType="primary" onClick={handleAddLockDateClick}>
                Add lock date
              </Button>
            </div>
          )}

          {lockDateData?.accounting_lock_date && (
            <div className="t-mt-4 t-text-body-sm t-text-text-30">
              <div>
                Locked by:{" "}
                <span className="t-font-bold">
                  {logsData?.logs?.[0]?.changed_by.name}
                </span>{" "}
                on{" "}
                <span className="t-font-bold">
                  {formatDate(logsData?.logs?.[0]?.created_at)}
                </span>
              </div>
            </div>
          )}
        </Async.Success>
      </Async.Root>

      {/* Edit Lock Date Modal */}
      <Modal.Root open={editModal.isOpen} onOpenChange={editModal.close}>
        <Modal.Content>
          <FormikForm
            initialValues={formValues || { accounting_lock_date: "" }}
            validationSchema={LockDateSchema}
            // @ts-ignore
            onSubmit={handleConfirmSubmit}
            enableReinitialize
          >
            {({
              values,
              isSubmitting,
              errors,
              isValid,
              validateForm,
              submitForm,
            }) => (
              <>
                <Modal.Header>
                  <Modal.Title>
                    {lockDateData?.accounting_lock_date
                      ? "Edit Lock Date"
                      : "Enter Lock Date"}
                  </Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body>
                  <div className="t-flex t-flex-col t-gap-4">
                    <Field name="accounting_lock_date">
                      {({ field, form }: any) => (
                        <DateInput
                          label="Lock date"
                          {...field}
                          portalId={field.name}
                          placeholder="DD-MMM-YYYY"
                        />
                      )}
                    </Field>
                  </div>
                </Modal.Body>
                <Modal.Footer className="t-flex t-justify-end t-gap-2">
                  {/* Confirm Lock Date Modal */}
                  <Modal.Root
                    open={confirmModal.isOpen}
                    onOpenChange={() => {
                      confirmModal.close();
                      editModal.close();
                    }}
                  >
                    <Modal.Content>
                      <Modal.Header>
                        <Modal.Title>
                          {lockDateData?.accounting_lock_date
                            ? "Edit Lock Date?"
                            : "Lock Financials?"}
                        </Modal.Title>
                        <Modal.Close />
                      </Modal.Header>
                      <Modal.Body>
                        <p className="t-text-body t-text-text-60 t-mb-0">
                          {lockDateData?.accounting_lock_date ? (
                            <span>
                              If you proceed, you or anyone won't be able to
                              make any data affecting transactions on or before{" "}
                              <span className="t-font-bold">
                                {values?.accounting_lock_date
                                  ? formatDate(values.accounting_lock_date)
                                  : ""}
                              </span>
                              .
                            </span>
                          ) : (
                            <span>
                              If you proceed, no edits will be allowed that
                              might impact the financials on or before{" "}
                              <span className="t-font-bold">
                                {values?.accounting_lock_date
                                  ? formatDate(values.accounting_lock_date)
                                  : ""}
                              </span>
                            </span>
                          )}
                        </p>
                        <p className="t-mt-4 t-text-body t-text-text-60 t-mb-0">
                          Do you want to continue?
                        </p>
                        <p className="t-mt-4 t-text-body-italic t-italic t-text-text-60 t-mb-0">
                          Note: If you need to make any changes later, you can
                          unlock the financials at any time.
                        </p>
                      </Modal.Body>
                      <Modal.FooterButtonGroup>
                        <Button onClick={confirmModal.close} type="button">
                          Go back
                        </Button>
                        <Button
                          customType="danger"
                          onClick={submitForm}
                          isLoading={isSubmitting || isUpdating}
                          disabled={isSubmitting || isUpdating}
                        >
                          Confirm
                        </Button>
                      </Modal.FooterButtonGroup>
                    </Modal.Content>
                  </Modal.Root>

                  <Button onClick={() => editModal.close()} type="button">
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    customType="primary"
                    onClick={async () => {
                      try {
                        const errors = await validateForm();
                        if (Object.keys(errors).length > 0) {
                          return;
                        }
                        confirmModal.open();
                      } catch (error) {}
                    }}
                    disabled={!isValid}
                  >
                    {lockDateData?.accounting_lock_date ? "Next" : "Proceed"}
                  </Button>
                </Modal.Footer>
              </>
            )}
          </FormikForm>
        </Modal.Content>
      </Modal.Root>

      <Modal.Root open={removeModal.isOpen} onOpenChange={removeModal.close}>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Remove Lock Date?</Modal.Title>
            <Modal.Close />
          </Modal.Header>

          <Modal.Body>
            <div className="t-text-body t-text-text-60 t-mb-0">
              Deleting the lock date will allow anyone with access to make
              changes to past financial data. This may affect previously closed
              books, reports, and tax filings.
              <br />
              <br />
              Do you want to continue?
            </div>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Button onClick={removeModal.close} type="button">
              Go back
            </Button>
            <Button
              customType="danger"
              onClick={confirmDeleteLock}
              isLoading={isUpdating}
              disabled={isUpdating}
            >
              Confirm
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
    </DashboardLayout>
  );
};

export default BooksLockDate;
